import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loading, EmptyState } from "@/components/common";
import PeriodFilter, { usePeriod } from "@/components/PeriodFilter";
import { rupiah, formatDate } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function GeneralLedger() {
  const { period, start_date, end_date, setPeriod } = usePeriod("month");
  const [coa, setCoa] = useState([]);
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/accounting/chart-of-accounts").then((r) => setCoa(r.data.filter((a) => a.allow_posting))); }, []);
  useEffect(() => {
    if (!code) { setData(null); return; }
    const p = { period, account_code: code };
    if (period === "custom") { p.start_date = start_date; p.end_date = end_date; }
    setLoading(true);
    api.get("/accounting/general-ledger", { params: p }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [code, period, start_date, end_date]);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="gl-title">Buku Besar</h1>
          <p className="text-sm text-slate-500 mt-1">Mutasi per akun dari jurnal yang sudah diposting.</p>
        </div>
        <PeriodFilter period={period} start_date={start_date} end_date={end_date} onChange={setPeriod} />
      </div>
      <div className="mb-4 max-w-md">
        <Select value={code} onValueChange={setCode}><SelectTrigger data-testid="gl-account"><SelectValue placeholder="Pilih akun..." /></SelectTrigger><SelectContent>{coa.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent></Select>
      </div>
      {!code ? <EmptyState title="Pilih akun untuk melihat buku besar" /> : loading ? <Loading /> : !data ? null : (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <div className="flex justify-between border-b border-slate-100 px-4 py-3 text-sm">
            <span className="font-semibold text-slate-800">{data.account.code} - {data.account.name}</span>
            <span className="text-slate-500">Saldo Awal: <b>{rupiah(data.beginning_balance)}</b></span>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>No Jurnal</TableHead><TableHead>Deskripsi</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Kredit</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.rows.map((r, i) => (
                <TableRow key={i} data-testid={`gl-row-${i}`}>
                  <TableCell className="text-xs text-slate-500">{formatDate(r.date)}</TableCell>
                  <TableCell className="font-mono text-xs">{r.journal_no}</TableCell>
                  <TableCell className="max-w-[260px] truncate text-sm">{r.description}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.debit ? rupiah(r.debit) : "-"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.credit ? rupiah(r.credit) : "-"}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{rupiah(r.balance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-end border-t border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">Saldo Akhir: {rupiah(data.ending_balance)}</div>
        </div>
      )}
    </div>
  );
}
